import React, { useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Button,
  Chip, CircularProgress, makeStyles, Avatar,
  Menu, MenuItem, Tooltip
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import WarningAmberIcon from '@material-ui/icons/Warning';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  const cleanServer = (process.env.REACT_APP_IMG_SERVER || '').replace(/\/+$/, '');
  const cleanUrl = url.replace(/^\/+/, '');
  return `${cleanServer}/${cleanUrl}`;
};

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
    '&.MuiPaper-elevation1': {
      boxShadow: 'none'
    }
  },
  tableWrapper: {
    '& .MuiTableCell-root': {
      borderBottom: '2px solid #F1F1F1 !important',
      padding: '8px 12px !important',
      fontSize: '14px'
    },
    overflow: 'auto',
    '&::-webkit-scrollbar': { width: '8px', height: '8px' },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#d2d9ddff',
      borderRadius: '8px'
    },
    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#bfc6caff' },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f1f1',
      borderRadius: '8px'
    }
  },
  codeChip: {
    fontFamily: 'monospace',
    fontWeight: 700,
    fontSize: 12
  },
  warningBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontWeight: 600,
    fontSize: 13
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 4,
    objectFit: 'cover',
    border: '1px solid #e0e0e0',
    margin: '0 auto'
  },
  tooltip: {
    backgroundColor: 'rgba(33, 37, 41, 0.98)',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.3)',
    borderRadius: 8,
    border: '1px solid rgba(255, 255, 255, 0.15)',
    padding: 0,
    maxWidth: 'none'
  },
  arrow: {
    color: 'rgba(33, 37, 41, 0.98)'
  }
}));

const ProductHoverCard = ({ product }) => {
  const primaryImage = product.images?.find(i => i.is_primary) || product.images?.[0];
  const placeholderSvg = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"><rect width="120" height="120" fill="%23f1f5f9"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="12" fill="%2394a3b8">Không có ảnh</text></svg>`;
  const imageUrl = primaryImage ? getImageUrl(primaryImage.url) : placeholderSvg;

  return (
    <Box p={1.5} display="flex" style={{ gap: 16, minWidth: 420, maxWidth: 500, color: '#fff' }}>
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center"
        style={{
          width: 120,
          height: 120,
          flexShrink: 0,
          backgroundColor: '#fff',
          borderRadius: 6,
          border: '1px solid rgba(255, 255, 255, 0.15)',
          overflow: 'hidden'
        }}
      >
        <img
          src={imageUrl}
          alt={product.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </Box>
      <Box display="flex" flexDirection="column" justifyContent="center" style={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#ffb3ba', fontSize: '14px', marginBottom: 6, whiteSpace: 'normal' }}>
          {product.name} {product.code ? `(${product.code})` : ''}
        </Typography>
        <Typography variant="body2" style={{ fontSize: 12, whiteSpace: 'pre-line', color: '#f8f9fa', lineHeight: 1.4 }}>
          <strong>Mô tả:</strong> {product.description || 'Chưa có mô tả'}
        </Typography>
        {product.includes && (
          <Typography variant="body2" style={{ fontSize: 12, whiteSpace: 'pre-line', color: '#e9ecef', marginTop: 4, lineHeight: 1.4 }}>
            <strong>Kèm theo:</strong> {product.includes}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ProductsTable = ({
  products = [],
  total,
  page,
  pageSize,
  isLoading,
  canWrite,
  isAdmin,
  onPageChange,
  onEdit,
  onDelete,
  onAdjustStock
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const handleOpenMenu = (event, product) => {
    setAnchorEl(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedProduct(null);
  };

  const getStockStatus = product => {
    const available = product.available_quantity ?? product.stock_available ?? null;
    const threshold = product.stock_warning_threshold;
    if (available === null) return null;
    if (available === 0) return 'empty';
    if (threshold && available <= threshold) return 'low';
    return 'ok';
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper} className={classes.root} style={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Box className={classes.tableWrapper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '4%', textAlign: 'center' }}>STT</TableCell>
                <TableCell style={{ width: '5%', textAlign: 'center' }}>Ảnh</TableCell>
                <TableCell style={{ width: '8%', textAlign: 'center' }}>Mã SP</TableCell>
                <TableCell style={{ width: '25%' }}>Tên sản phẩm</TableCell>
                <TableCell style={{ width: '24%' }}>Kèm theo</TableCell>
                <TableCell style={{ width: '6%', textAlign: 'center' }}>Đơn vị</TableCell>
                <TableCell style={{ width: '10%', textAlign: 'center' }}>Tồn kho</TableCell>
                {canWrite && (
                  <TableCell style={{ width: '10%', textAlign: 'center' }}>Nhập kho</TableCell>
                )}
                {isAdmin && (
                  <TableCell style={{ width: '8%', textAlign: 'center' }}>Thao tác</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 9 : (canWrite ? 8 : 7)}>
                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" width="100%" py={4}>
                      <Box mb={1}>
                        <img src="/static/empty.png" alt="empty" style={{ height: 80, opacity: 0.6 }} />
                      </Box>
                      <Typography>
                        <h4>Danh sách trống</h4>
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product, idx) => {
                  const stockStatus = getStockStatus(product);
                  const primaryImage = product.images?.find(i => i.is_primary) || product.images?.[0];

                  return (
                    <TableRow hover key={product.id}>
                      <TableCell style={{ textAlign: 'center' }}>
                        {(page - 1) * pageSize + idx + 1}
                      </TableCell>
                      <TableCell style={{ textAlign: 'center' }}>
                        <Tooltip
                          title={<ProductHoverCard product={product} />}
                          interactive
                          arrow
                          placement="right"
                          classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                        >
                          <span style={{ cursor: 'pointer', display: 'inline-block' }}>
                            {primaryImage ? (
                              <img
                                src={getImageUrl(primaryImage.url)}
                                alt={product.name}
                                className={classes.avatar}
                              />
                            ) : (
                              <Avatar
                                className={classes.avatar}
                                style={{ fontSize: 13 }}
                              >
                                SP
                              </Avatar>
                            )}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell style={{ textAlign: 'center' }}>
                        {product.code || '—'}
                      </TableCell>
                      <TableCell>
                        {product.name}
                      </TableCell>
                      <TableCell>
                        {product.includes ? (
                          <span style={{
                            display: 'block',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal'
                          }}>
                            {product.includes}
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{product.unit || '—'}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>
                        {stockStatus === 'low' && (
                          <Box className={classes.warningBadge} style={{ justifyContent: 'center' }}>
                            <WarningAmberIcon style={{ fontSize: 14 }} />
                            {product.available_quantity ?? product.stock_available ?? '?'}
                          </Box>
                        )}
                        {stockStatus === 'empty' && (
                          <span>Hết hàng</span>
                        )}
                        {stockStatus === 'ok' && (
                          <span>
                            {product.available_quantity ?? product.stock_available}
                          </span>
                        )}
                        {stockStatus === null && (
                          <span>—</span>
                        )}
                      </TableCell>
                      {canWrite && (
                        <TableCell style={{ textAlign: 'center' }}>
                          <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={() => onAdjustStock(product)}
                            style={{ textTransform: 'none', fontSize: 12, padding: '4px 10px', height: 28 }}
                          >
                            Nhập kho
                          </Button>
                        </TableCell>
                      )}
                      {isAdmin && (
                        <TableCell style={{ textAlign: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, product)}
                            style={{ color: '#AEB1C5' }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>

      {/* Action Menu Popover */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        keepMounted
      >

        <MenuItem
          onClick={() => {
            if (selectedProduct) onEdit(selectedProduct);
            handleCloseMenu();
          }}
          style={{ fontSize: 13, fontWeight: 500 }}
        >
          Chỉnh sửa
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedProduct) onDelete(selectedProduct);
            handleCloseMenu();
          }}
          style={{ fontSize: 13, fontWeight: 500, color: '#e74c3c' }}
        >
          Xóa
        </MenuItem>
      </Menu>

      {products.length > 0 && (
        <div className="mt-3 mr-4 float-right">
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '15px'
            }}
          >
            <Typography style={{ fontSize: '14px' }}>
              Tổng: {total}
            </Typography>
            <Pagination
              count={Math.ceil(total / pageSize)}
              color="primary"
              size="small"
              onChange={(_, p) => onPageChange(p)}
              page={page}
              siblingCount={1}
              boundaryCount={2}
              showFirstButton
              showLastButton
            />
          </Box>
        </div>
      )}
    </>
  );
};

export default ProductsTable;
